import {
  Component,
  Input,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";

import {
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { CrudAbstractFormComponent } from "../../crud-abstract/crud-abstract-form.component";
import { TOAST } from "app/notify/toast/toast-type.enum";
import { BackendErrorPipe } from "app/shared/backend/backend-error.pipe";
import { UserService } from "app/user/user.service";
import { noop } from "app/shared/noop.function";
import { User } from "app/user/user.model";
import { ClientPotential } from "app/potential/client-potential.model";
import { ClientPotentialRepository } from "app/potential/client-potential.repository";
import { ClientPotentialMapper } from "app/potential/client-potential.mapper";
import { transportType } from "app/shared/transport-type/transport-type.const";
import { loadType } from "app/shared/load-type/load-type.const";
import { BaseStandardQuote } from "app/quote/models/quote-base-standard.model";
import { FclQuote } from "app/quote/models/quote-fcl.model";
import { LclQuote } from "app/quote/models/quote-lcl.model";
import { AirQuote } from "app/quote/models/quote-air.model";
import { LtlQuote } from "app/quote/models/quote-ltl.model";
import { FtlQuote } from "app/quote/models/quote-ftl.model";
import { AirTariffQuote } from "app/quote/models/quote-airtariff.model";
import { locationKind } from "app/location/location-kind.const";
import { unitType } from "app/shared/unit-type/unit-type.const";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Dialog } from "primeng/dialog";
import { InputText } from "primeng/inputtext";
import { UnitTypeSelectComponent } from "../../ui/dropdown/unit-type-select/unit-type-select.component";

@Component({
  selector: "app-client-potential-form",
  templateUrl: "./client-potential-form.component.html",
  styleUrls: ["./client-potential-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InputText,
    ButtonDirective,
    Bind,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    UnitTypeSelectComponent,
  ],
})
export class ClientPotentialFormComponent
  extends CrudAbstractFormComponent
  implements OnInit
{
  protected userService = inject(UserService);

  @Input() public model: ClientPotential;
  public readonly quote = input<BaseStandardQuote>(undefined);
  readonly closeView = output();
  public user: User = null;
  public unitType = unitType;

  constructor() {
    const repository = inject(ClientPotentialRepository);
    super();
    this.repository = repository;
  }

  public ngOnInit() {
    super.ngOnInit();
    this.setUser();
  }

  public createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      id: [this.model ? this.model.id : null],
      volume: [this.model ? this.model.volume : null, Validators.required],
      comment: [this.model ? this.model.comment : null],
      unitType: [this.getUnitType()],
    });
  }

  public prepareSaveModel(form: UntypedFormGroup): ClientPotential {
    const model: ClientPotential = ClientPotentialMapper.map({
      id: form.value.id,
      volume: form.value.volume,
      comment: form.value.comment,
      unitType: form.value.unitType,
      transportType: this.getTransportType(),
      loadType: this.getLoadType(),
    });
    // TODO: We have to handle quote with more than one client
    model.client = this.quote().getClients()[0];
    // TODO: We have to handle quote with one origin or destination
    model.originCountry = this.quote().getOriginLocations()[0].getLocation()
      ? this.quote().getOriginLocations()[0].getLocation().getCountry()
      : this.quote().getOriginLocations()[0].getCountry();
    model.originLocation = this.quote().getOriginLocations()[0].getLocation();
    model.destinationCountry = this.quote()
      .getDestinationLocations()[0]
      .getLocation()
      ? this.quote().getDestinationLocations()[0].getLocation().getCountry()
      : this.quote().getDestinationLocations()[0].getCountry();
    model.destinationLocation = this.quote()
      .getDestinationLocations()[0]
      .getLocation();

    return model;
  }

  public updateValues(): void {
    this.form.get("status");
  }

  public add() {
    this.model = this.prepareSaveModel(this.form);
    if (!this.model.isValid()) {
      this.notifyService.toast(
        TOAST.ERROR,
        "Can't add this item",
        "This account potential is missing required details.",
      );

      return;
    }

    this.loading = true;
    this.subscription.add(
      this.repository.add(this.model).subscribe({
        next: (response): void => {
          this.closeView.emit();
        },
        error: (err): void => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Can't add this item",
            new BackendErrorPipe().transform(err, "string"),
          );
          this.loading = false;
        },
      }),
    );
  }

  private setUser() {
    this.userService.getUser().then((user) => (this.user = user), noop);
  }

  public close() {
    this.closeView.emit();
  }

  public getUnitType() {
    const quote = this.quote();
    if (quote instanceof FclQuote) {
      return unitType.TEU;
    } else if (quote instanceof LclQuote) {
      return unitType.CBM;
    } else if (quote instanceof AirQuote || quote instanceof AirTariffQuote) {
      return unitType.TON;
    } else if (quote instanceof LtlQuote || quote instanceof FtlQuote) {
      return unitType.CBM;
    }
  }

  private getTransportType() {
    const quote = this.quote();
    if (quote.getKind() == locationKind.port) {
      return transportType.SEA;
    } else if (quote.getKind() == locationKind.airport) {
      return transportType.AIR;
    } else if (quote.getKind() == locationKind.railTerminal) {
      return transportType.RAIL;
    } else if (quote.getKind() == locationKind.roadTerminal) {
      return transportType.ROAD;
    }
  }

  private getLoadType() {
    const quote = this.quote();
    if (quote instanceof FclQuote) {
      return loadType.FCL;
    } else if (quote instanceof LclQuote) {
      return loadType.LCL;
    } else if (quote instanceof AirQuote || quote instanceof AirTariffQuote) {
      return loadType.AIR;
    } else if (quote instanceof LtlQuote) {
      return loadType.LTL;
    } else if (quote instanceof FtlQuote) {
      return loadType.FTL;
    }
  }
}
